'use client'

import { Pagination } from '@/General/Pagination'
import { buildSaleUrl, type SaleSortSlug } from '@/saleUrl'
import type { SaleSectionSlug } from '@/saleSections'
import { ArrowUpIcon } from '@heroicons/react/20/solid'
import type { MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SaleProductRowSkeleton } from './SaleProductRowSkeleton'
import { SaleProductRows } from './SaleProductRows'
import type { SaleProductsPage, SaleProductRow } from './saleProducts'

const BACK_TO_TOP_THRESHOLD_PAGES = 3
const SKELETON_ROW_COUNT = 3
const SALE_LIST_STATE_VERSION = 1
const SALE_LIST_STATE_TTL_MS = 30 * 60 * 1000

type LoadedSalePage = {
  page: number
  products: SaleProductRow[]
}

type SalePageCacheEntry = Promise<SaleProductsPage> | SaleProductsPage

type StoredSaleListState = {
  version: number
  savedAt: number
  activePage: number
  nextPage: number
  knownTotalPages: number
  knownTotalProducts: number
  scrollY: number
  loadedPages: LoadedSalePage[]
}

type CurrentSaleListState = Pick<
  StoredSaleListState,
  | 'activePage'
  | 'nextPage'
  | 'knownTotalPages'
  | 'knownTotalProducts'
  | 'loadedPages'
>

type SaleInfiniteProductListProps = {
  brandCode: string
  filters: string[]
  initialPage: number
  initialProducts: SaleProductRow[]
  perPage: number
  saleSlug: SaleSectionSlug
  sort: SaleSortSlug
  totalPages: number
  totalProducts: number
}

const getItemRangeLabel = (page: number, count: number, perPage: number) => {
  const start = (page - 1) * perPage + 1
  const end = start + count - 1
  return `Items ${start}–${end}`
}

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updatePreference = () => {
      setPrefersReducedMotion(mediaQuery.matches)
    }

    updatePreference()
    mediaQuery.addEventListener('change', updatePreference)

    return () => mediaQuery.removeEventListener('change', updatePreference)
  }, [])

  return prefersReducedMotion
}

export const SaleInfiniteProductList = ({
  brandCode,
  filters,
  initialPage,
  initialProducts,
  perPage,
  saleSlug,
  sort,
  totalPages,
  totalProducts,
}: SaleInfiniteProductListProps) => {
  const filtersKey = filters.join(',')
  const listStateKey = useMemo(
    () =>
      [
        'vi-sale-list',
        SALE_LIST_STATE_VERSION,
        brandCode,
        saleSlug,
        sort,
        filtersKey,
        initialPage,
        perPage,
      ].join(':'),
    [brandCode, filtersKey, initialPage, perPage, saleSlug, sort]
  )
  const prefersReducedMotion = usePrefersReducedMotion()
  const [loadedPages, setLoadedPages] = useState<LoadedSalePage[]>([
    { page: initialPage, products: initialProducts },
  ])
  const [activePage, setActivePage] = useState(initialPage)
  const [nextPage, setNextPage] = useState(initialPage + 1)
  const [knownTotalPages, setKnownTotalPages] = useState(totalPages)
  const [knownTotalProducts, setKnownTotalProducts] = useState(totalProducts)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const pageSectionsRef = useRef(new Map<number, HTMLElement>())
  const loadedPageNumbersRef = useRef(new Set([initialPage]))
  const pageCacheRef = useRef(new Map<number, SalePageCacheEntry>())
  const preloadedThumbnailUrlsRef = useRef(new Set<string>())
  const activePageRef = useRef(initialPage)
  const pendingManualPageRef = useRef<number | null>(null)
  const restoredScrollYRef = useRef<number | null>(null)
  const lastRestoredSavedAtRef = useRef<number | null>(null)
  const scrollRafRef = useRef<number | null>(null)
  const isLoadingRef = useRef(false)
  const latestListStateRef = useRef<CurrentSaleListState>({
    activePage: initialPage,
    nextPage: initialPage + 1,
    knownTotalPages: totalPages,
    knownTotalProducts: totalProducts,
    loadedPages: [{ page: initialPage, products: initialProducts }],
  })

  const sortedLoadedPages = useMemo(
    () => [...loadedPages].sort((pageA, pageB) => pageA.page - pageB.page),
    [loadedPages]
  )

  const loadedProductCount = useMemo(
    () =>
      sortedLoadedPages.reduce((sum, page) => sum + page.products.length, 0),
    [sortedLoadedPages]
  )

  const remainingProducts = Math.max(0, knownTotalProducts - loadedProductCount)

  const linkGenerator = useCallback(
    (page: number) =>
      buildSaleUrl({
        saleSlug,
        filters,
        sort,
        page,
      }),
    [filters, saleSlug, sort]
  )

  const pageOneHref = linkGenerator(1)

  const nextPageHref =
    nextPage <= knownTotalPages ? linkGenerator(nextPage) : null

  useEffect(() => {
    latestListStateRef.current = {
      activePage,
      nextPage,
      knownTotalPages,
      knownTotalProducts,
      loadedPages: sortedLoadedPages,
    }
  }, [
    activePage,
    knownTotalPages,
    knownTotalProducts,
    nextPage,
    sortedLoadedPages,
  ])

  const buildApiUrl = useCallback(
    (page: number) => {
      const params = new URLSearchParams({
        brandCode,
        page: String(page),
        saleSlug,
        sort,
      })

      filters.forEach(filter => params.append('filter', filter))

      return `/api/sale-products/?${params.toString()}`
    },
    [brandCode, filters, saleSlug, sort]
  )

  const preloadProductThumbnails = useCallback((products: SaleProductRow[]) => {
    if (typeof window === 'undefined') {
      return
    }

    products.slice(0, 4).forEach(product => {
      if (!product.thumbnail) {
        return
      }

      const thumbnailUrl = `${product.thumbnail}?w=128&dpr=2`

      if (preloadedThumbnailUrlsRef.current.has(thumbnailUrl)) {
        return
      }

      preloadedThumbnailUrlsRef.current.add(thumbnailUrl)
      const image = new window.Image()
      image.decoding = 'async'
      image.src = thumbnailUrl
    })
  }, [])

  const fetchSalePage = useCallback(
    async (pageToFetch: number) => {
      const cachedPage = pageCacheRef.current.get(pageToFetch)

      if (cachedPage) {
        return await Promise.resolve(cachedPage)
      }

      const pageRequest = fetch(buildApiUrl(pageToFetch), {
        headers: { Accept: 'application/json' },
      })
        .then(async response => {
          if (!response.ok) {
            throw new Error(
              `Sale page ${pageToFetch} returned ${response.status}`
            )
          }

          const salePage = (await response.json()) as SaleProductsPage

          pageCacheRef.current.set(pageToFetch, salePage)
          preloadProductThumbnails(salePage.products)

          return salePage
        })
        .catch(error => {
          pageCacheRef.current.delete(pageToFetch)
          throw error
        })

      pageCacheRef.current.set(pageToFetch, pageRequest)

      return await pageRequest
    },
    [buildApiUrl, preloadProductThumbnails]
  )

  useEffect(() => {
    if (typeof window === 'undefined' || initialPage !== 1) {
      return
    }

    try {
      const rawState = window.sessionStorage.getItem(listStateKey)

      if (!rawState) {
        return
      }

      const parsed = JSON.parse(rawState) as Partial<StoredSaleListState>
      const savedAt = Number(parsed.savedAt)

      if (
        parsed.version !== SALE_LIST_STATE_VERSION ||
        !Number.isFinite(savedAt) ||
        Date.now() - savedAt > SALE_LIST_STATE_TTL_MS ||
        !Array.isArray(parsed.loadedPages)
      ) {
        window.sessionStorage.removeItem(listStateKey)
        return
      }

      const restoredPages = parsed.loadedPages
        .filter(
          (page): page is LoadedSalePage =>
            Number.isInteger(page?.page) &&
            page.page >= 1 &&
            Array.isArray(page.products)
        )
        .sort((pageA, pageB) => pageA.page - pageB.page)

      if (restoredPages.length === 0) {
        return
      }

      const restoredScrollY = Math.max(0, Number(parsed.scrollY) || 0)
      const shouldRestoreScroll = restoredScrollY > 0 && window.scrollY < 8

      if (
        lastRestoredSavedAtRef.current === savedAt ||
        restoredPages.length < loadedPages.length ||
        (restoredPages.length === loadedPages.length && !shouldRestoreScroll)
      ) {
        return
      }

      const pagesByNumber = new Map<number, LoadedSalePage>([
        [initialPage, { page: initialPage, products: initialProducts }],
      ])

      restoredPages.forEach(page => pagesByNumber.set(page.page, page))

      const nextLoadedPages = Array.from(pagesByNumber.values()).sort(
        (pageA, pageB) => pageA.page - pageB.page
      )
      const restoredPageNumbers = new Set(
        nextLoadedPages.map(page => page.page)
      )
      const restoredTotalPages =
        Number(parsed.knownTotalPages) || knownTotalPages
      const restoredTotalProducts =
        Number(parsed.knownTotalProducts) || knownTotalProducts
      const maxLoadedPage = Math.max(...restoredPageNumbers)
      const restoredNextPage = Math.min(
        restoredTotalPages + 1,
        Math.max(
          Number(parsed.nextPage) || maxLoadedPage + 1,
          maxLoadedPage + 1
        )
      )
      const restoredActivePage = restoredPageNumbers.has(
        Number(parsed.activePage)
      )
        ? Number(parsed.activePage)
        : maxLoadedPage

      lastRestoredSavedAtRef.current = savedAt

      window.setTimeout(() => {
        loadedPageNumbersRef.current = restoredPageNumbers
        nextLoadedPages.forEach(page => {
          pageCacheRef.current.set(page.page, {
            page: page.page,
            products: page.products,
            totalProducts: restoredTotalProducts,
            totalPages: restoredTotalPages,
            hasPreviousPage: page.page > 1,
            hasNextPage: page.page < restoredTotalPages,
          })
        })

        setLoadedPages(nextLoadedPages)
        setKnownTotalPages(restoredTotalPages)
        setKnownTotalProducts(restoredTotalProducts)
        setNextPage(restoredNextPage)
        activePageRef.current = restoredActivePage
        setActivePage(restoredActivePage)
        restoredScrollYRef.current = restoredScrollY
      }, 0)
    } catch {
      window.sessionStorage.removeItem(listStateKey)
    }
  }, [
    initialPage,
    initialProducts,
    knownTotalPages,
    knownTotalProducts,
    loadedPages.length,
    listStateKey,
  ])

  const mergeLoadedPage = useCallback((salePage: SaleProductsPage) => {
    if (loadedPageNumbersRef.current.has(salePage.page)) {
      return
    }

    loadedPageNumbersRef.current.add(salePage.page)
    setLoadedPages(currentPages => {
      const nextPages = [
        ...currentPages,
        { page: salePage.page, products: salePage.products },
      ]
      nextPages.sort((pageA, pageB) => pageA.page - pageB.page)
      return nextPages
    })
  }, [])

  const loadNextPage = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (
        isLoadingRef.current ||
        nextPage > knownTotalPages ||
        (loadError && !force)
      ) {
        return false
      }

      const pageToLoad = nextPage
      isLoadingRef.current = true
      setIsFetchingNextPage(true)
      setLoadError(null)

      try {
        const salePage = await fetchSalePage(pageToLoad)

        mergeLoadedPage(salePage)

        setKnownTotalPages(salePage.totalPages)
        setKnownTotalProducts(salePage.totalProducts)
        setNextPage(
          salePage.hasNextPage ? salePage.page + 1 : salePage.totalPages + 1
        )

        if (force) {
          pendingManualPageRef.current = salePage.page

          activePageRef.current = salePage.page
          setActivePage(salePage.page)
        }

        return true
      } catch {
        setLoadError('Unable to load more deals right now.')
        return false
      } finally {
        isLoadingRef.current = false
        setIsFetchingNextPage(false)
      }
    },
    [fetchSalePage, knownTotalPages, loadError, mergeLoadedPage, nextPage]
  )

  const preloadPage = useCallback(
    (pageToPreload: number) => {
      if (
        pageToPreload > knownTotalPages ||
        loadedPageNumbersRef.current.has(pageToPreload)
      ) {
        return
      }

      void fetchSalePage(pageToPreload).catch(() => {})
    },
    [fetchSalePage, knownTotalPages]
  )

  const setCurrentPage = useCallback((page: number) => {
    if (activePageRef.current !== page) {
      activePageRef.current = page
      setActivePage(page)
    }
  }, [])

  const saveListState = useCallback(() => {
    if (typeof window === 'undefined' || initialPage !== 1) {
      return
    }

    const latestState = latestListStateRef.current

    const state: StoredSaleListState = {
      version: SALE_LIST_STATE_VERSION,
      savedAt: Date.now(),
      activePage: latestState.activePage,
      nextPage: latestState.nextPage,
      knownTotalPages: latestState.knownTotalPages,
      knownTotalProducts: latestState.knownTotalProducts,
      scrollY: window.scrollY,
      loadedPages: latestState.loadedPages,
    }

    try {
      window.sessionStorage.setItem(listStateKey, JSON.stringify(state))
    } catch {
      // Storage can fill up on very deep browsing; the sale still works without restore.
    }
  }, [initialPage, listStateKey])

  const updateActivePageFromScroll = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    const pageSections = Array.from(pageSectionsRef.current.entries()).sort(
      ([pageA], [pageB]) => pageA - pageB
    )

    if (pageSections.length === 0) {
      return
    }

    const viewportAnchor = window.innerHeight * 0.38
    let currentVisiblePage = pageSections[0]?.[0] ?? initialPage

    pageSections.forEach(([sectionPage, element]) => {
      if (element.getBoundingClientRect().top <= viewportAnchor) {
        currentVisiblePage = sectionPage
      }
    })

    setCurrentPage(currentVisiblePage)
    setShowBackToTop(
      sortedLoadedPages.length >= BACK_TO_TOP_THRESHOLD_PAGES &&
        window.scrollY > window.innerHeight
    )
  }, [initialPage, setCurrentPage, sortedLoadedPages.length])

  const handleLoadMoreClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void loadNextPage({ force: true })
  }

  const handleBackToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    })
  }

  const registerPageSection = useCallback(
    (page: number, element: HTMLElement | null) => {
      if (element) {
        pageSectionsRef.current.set(page, element)
      } else {
        pageSectionsRef.current.delete(page)
      }
    },
    []
  )

  const loadMoreLabel = loadError
    ? 'Try again'
    : remainingProducts > 0
      ? `Show ${Math.min(perPage, remainingProducts)} more deal${
          Math.min(perPage, remainingProducts) === 1 ? '' : 's'
        }`
      : 'Show more deals'

  useEffect(() => {
    const node = loadMoreRef.current

    if (
      !node ||
      nextPage > knownTotalPages ||
      loadError ||
      prefersReducedMotion ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          void loadNextPage()
        }
      },
      { rootMargin: '700px 0px' }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [knownTotalPages, loadError, loadNextPage, nextPage, prefersReducedMotion])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const scheduleActivePageUpdate = () => {
      if (scrollRafRef.current !== null) {
        return
      }

      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null
        updateActivePageFromScroll()
      })
    }

    scheduleActivePageUpdate()
    window.addEventListener('scroll', scheduleActivePageUpdate, {
      passive: true,
    })
    window.addEventListener('resize', scheduleActivePageUpdate)

    return () => {
      window.removeEventListener('scroll', scheduleActivePageUpdate)
      window.removeEventListener('resize', scheduleActivePageUpdate)

      if (scrollRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRafRef.current)
        scrollRafRef.current = null
      }
    }
  }, [filtersKey, sortedLoadedPages, updateActivePageFromScroll])

  useEffect(() => {
    if (typeof window === 'undefined' || initialPage !== 1) {
      return
    }

    let saveTimer: number | null = null

    const scheduleSave = () => {
      if (saveTimer !== null) {
        window.clearTimeout(saveTimer)
      }

      saveTimer = window.setTimeout(saveListState, 150)
    }

    const saveOnHidden = () => {
      if (document.visibilityState === 'hidden') {
        saveListState()
      }
    }

    const saveOnProductLink = (event: Event) => {
      const target = event.target

      if (target instanceof Element && target.closest('[data-product-link]')) {
        saveListState()
      }
    }

    saveListState()
    window.addEventListener('scroll', scheduleSave, { passive: true })
    window.addEventListener('resize', scheduleSave)
    window.addEventListener('pagehide', saveListState)
    document.addEventListener('visibilitychange', saveOnHidden)
    document.addEventListener('click', saveOnProductLink, true)

    return () => {
      if (saveTimer !== null) {
        window.clearTimeout(saveTimer)
      }

      window.removeEventListener('scroll', scheduleSave)
      window.removeEventListener('resize', scheduleSave)
      window.removeEventListener('pagehide', saveListState)
      document.removeEventListener('visibilitychange', saveOnHidden)
      document.removeEventListener('click', saveOnProductLink, true)
    }
  }, [initialPage, saveListState])

  useEffect(() => {
    saveListState()
  }, [
    activePage,
    knownTotalPages,
    knownTotalProducts,
    nextPage,
    saveListState,
    sortedLoadedPages,
  ])

  useEffect(() => {
    if (typeof window === 'undefined' || restoredScrollYRef.current === null) {
      return
    }

    const scrollY = restoredScrollYRef.current
    restoredScrollYRef.current = null

    let timeoutId: number | null = null

    const animationFrameId = window.requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, behavior: 'auto' })

      timeoutId = window.setTimeout(() => {
        window.scrollTo({ top: scrollY, behavior: 'auto' })
      }, 150)
    })

    return () => {
      window.cancelAnimationFrame(animationFrameId)

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [sortedLoadedPages])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const pendingPage = pendingManualPageRef.current

    if (pendingPage === null) {
      return
    }

    const section = pageSectionsRef.current.get(pendingPage)

    if (!section) {
      return
    }

    pendingManualPageRef.current = null

    window.requestAnimationFrame(() => {
      const sectionTop = section.getBoundingClientRect().top + window.scrollY

      window.scrollTo({
        top: Math.max(0, sectionTop - 16),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      })

      activePageRef.current = pendingPage
      setActivePage(pendingPage)
    })
  }, [prefersReducedMotion, sortedLoadedPages])

  useEffect(() => {
    if (nextPage > knownTotalPages || loadError || prefersReducedMotion) {
      return
    }

    const preloadTimer = window.setTimeout(() => {
      preloadPage(nextPage)
    }, 250)

    return () => window.clearTimeout(preloadTimer)
  }, [knownTotalPages, loadError, nextPage, preloadPage, prefersReducedMotion])

  useEffect(() => {
    if (typeof window === 'undefined' || initialPage <= 1) {
      return
    }

    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [initialPage, filtersKey, saleSlug, sort])

  if (knownTotalProducts === 0) {
    return null
  }

  return (
    <>
      {initialPage > 1 && (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-base text-gray-800">
          <p>
            You&apos;re viewing page {initialPage} of {knownTotalPages}. Earlier
            deals aren&apos;t shown above.
          </p>
          <a
            href={pageOneHref}
            className="mt-2 inline-block font-semibold text-vi-red hover:underline"
          >
            View from the beginning
          </a>
        </div>
      )}

      <div>
        {sortedLoadedPages.map(loadedPage => (
          <section
            key={loadedPage.page}
            ref={element => registerPageSection(loadedPage.page, element)}
            data-sale-page={loadedPage.page}
            aria-label={`Sale products page ${loadedPage.page}`}
          >
            {loadedPage.page !== initialPage && (
              <p className="sr-only">
                <a href={linkGenerator(loadedPage.page)}>
                  {getItemRangeLabel(
                    loadedPage.page,
                    loadedPage.products.length,
                    perPage
                  )}
                </a>
              </p>
            )}
            <SaleProductRows products={loadedPage.products} />
          </section>
        ))}
      </div>

      {(nextPageHref || isFetchingNextPage || loadError) && (
        <div
          ref={loadMoreRef}
          className="flex flex-col items-center gap-2 py-4"
        >
          {isFetchingNextPage ? (
            <div aria-live="polite" className="w-full">
              {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                <SaleProductRowSkeleton key={index} />
              ))}
            </div>
          ) : nextPageHref ? (
            <>
              {prefersReducedMotion && (
                <p className="text-center text-sm text-gray-500">
                  Load more deals using the button below.
                </p>
              )}
              <a
                href={nextPageHref}
                onClick={handleLoadMoreClick}
                aria-label={`${loadMoreLabel}, page ${nextPage} of ${knownTotalPages}`}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                {loadMoreLabel}
              </a>
              {loadError && (
                <p className="text-center text-sm text-red-600" role="alert">
                  {loadError}
                </p>
              )}
            </>
          ) : null}
        </div>
      )}

      <nav className="mt-8" aria-label="Sale listing pages">
        <Pagination
          totalCount={knownTotalProducts}
          perPage={perPage}
          page={activePage}
          linkGenerator={linkGenerator}
          hasPreviousPage={activePage > 1}
          hasNextPage={activePage < knownTotalPages}
        />
      </nav>

      {showBackToTop && (
        <button
          type="button"
          onClick={handleBackToTop}
          className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          aria-label="Back to top"
        >
          <ArrowUpIcon className="size-4" aria-hidden="true" />
          Top
        </button>
      )}
    </>
  )
}
